import { z } from 'zod';
import { randomBytes, sha256Hex, toBase64Url } from './crypto.js';
/**
 * Capability links for customer pages.
 *
 * A customer has no account, so the URL is the credential. The token is 256
 * bits of randomness; the database stores only its SHA-256 hash, so a leaked
 * database backup does not hand anyone a working link. Everything about how a
 * link is validated lives in app.resolve_public_link — this module only mints
 * tokens and builds URLs.
 */
export const PUBLIC_LINK_PURPOSES = [
    'quote',
    'invoice',
    'appointment',
    'change_order',
    'customer_hub',
];
/** URL prefix per purpose, kept short because these are typed by hand sometimes. */
export const PURPOSE_PATH = {
    quote: 'q',
    invoice: 'i',
    appointment: 'a',
    change_order: 'c',
    customer_hub: 'h',
};
export const PUBLIC_ACTIONS = [
    'view',
    'accept',
    'decline',
    'pay',
    'pay_deposit',
    'confirm',
    'update_access',
    'request_reschedule',
    'request_cancel',
    'request_service',
];
/** What a quote link permits today. Payment arrives with Stripe. */
export const QUOTE_LINK_ACTIONS = ['view', 'accept', 'decline'];
/** Pay is added only while the tenant has a charge- and payout-enabled Stripe account. */
export const INVOICE_LINK_ACTIONS = ['view', 'pay'];
/** Customer actions are requests; only confirmation changes appointment state. */
export const APPOINTMENT_LINK_ACTIONS = [
    'view',
    'confirm',
    'update_access',
    'request_reschedule',
    'request_cancel',
];
/** 32 random bytes, base64url for the URL and SHA-256 hex for the row. */
export async function mintLinkToken() {
    const token = toBase64Url(randomBytes(32));
    return { token, tokenHash: await sha256Hex(token) };
}
export const hashLinkToken = (token) => sha256Hex(token);
export function publicLinkUrl(baseUrl, purpose, token) {
    return `${baseUrl.replace(/\/+$/, '')}/${PURPOSE_PATH[purpose]}/${token}`;
}
/**
 * A visitor's IP, keyed-hashed rather than stored. The key rotates, so the
 * hashes stop being linkable to an address once it does.
 */
export async function hashClientIp(key, ip) {
    if (!ip)
        return undefined;
    return sha256Hex(`${key}:${ip}`);
}
/** POSTed by the quote page. */
export const PublicActionRequest = z.object({
    action: z.enum([
        'accept',
        'decline',
        'confirm',
        'update_access',
        'request_reschedule',
        'request_cancel',
        'request_service',
    ]),
    typed_name: z.string().max(120).optional(),
    note: z.string().max(2000).optional(),
    package_key: z.string().max(40).optional(),
    addon_keys: z.array(z.string().max(40)).max(20).optional(),
    requested_date: z.string().max(10).optional(),
    requested_time: z.string().max(5).optional(),
    job_id: z.string().uuid().optional(),
    preferred_date: z.string().max(10).optional(),
    idempotency_key: z.string().min(8).max(80).optional(),
});
//# sourceMappingURL=links.js.map